import React from "react";
import { Card, Alert } from "react-bootstrap";

function PermissionLookups() {
  return (
    <div>
      <h2 className="mb-4">
        Permission Lookups
      </h2>

      <Alert variant="info">
        Learn how to use the reverse lookup tools to find user permissions and runbook access.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Overview</h4>
          <p>
            The Permissions page includes two powerful lookup tabs that let you answer common access questions:
          </p>
          <ul>
            <li><strong>User Permissions Lookup</strong> - "What can this user do?"</li>
            <li><strong>Runbook Access Lookup</strong> - "Who can access this runbook?"</li>
          </ul>
          <p className="mt-3">
            These lookup tools help administrators understand the effective permissions in their system
            without manually checking each group.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>User Permissions Lookup</h4>
          <p>
            This tab shows you all the effective permissions a specific user has based on their group memberships.
          </p>

          <h6 className="mt-3">How to Use:</h6>
          <ol>
            <li>Go to <strong>Permissions</strong> in the Settings section</li>
            <li>Click the <strong>User Permissions Lookup</strong> tab</li>
            <li>Select a user from the dropdown (searchable)</li>
            <li>View their group memberships and effective permissions</li>
          </ol>

          <h6 className="mt-3">What You'll See:</h6>
          <ul>
            <li><strong>Group Memberships</strong> - All groups the user belongs to with active/inactive status</li>
            <li><strong>Permission Count</strong> - How many permissions each group grants</li>
            <li><strong>Effective Permissions</strong> - All unique permissions the user has, organized by category</li>
            <li><strong>Group Badges</strong> - Which group(s) grant each specific permission</li>
            <li><strong>Total Count</strong> - Total number of unique permissions across all groups</li>
          </ul>

          <h6 className="mt-3">Understanding Effective Permissions:</h6>
          <p>
            Permissions are <em>additive</em>. If a user is in multiple groups, they get all permissions from
            all groups combined. Duplicate permissions are automatically removed, so you see only unique permissions.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Runbook Access Lookup</h4>
          <p>
            This tab shows you which groups have access to a specific runbook and which users can see it.
          </p>

          <h6 className="mt-3">How to Use:</h6>
          <ol>
            <li>Go to <strong>Permissions</strong> in the Settings section</li>
            <li>Click the <strong>Runbook Access Lookup</strong> tab</li>
            <li>Select a runbook from the dropdown (searchable)</li>
            <li>View which groups have access and all users with access</li>
          </ol>

          <h6 className="mt-3">What You'll See:</h6>
          <ul>
            <li><strong>Permission Groups with Access</strong> - All groups that have this runbook assigned</li>
            <li><strong>Group Status</strong> - Whether each group is Active or Inactive</li>
            <li><strong>User Count</strong> - How many users are in each group</li>
            <li><strong>Users with Access</strong> - Complete list of all users who can see this runbook</li>
            <li><strong>User Details</strong> - Name, email, and which group(s) grant them access</li>
            <li><strong>Total Count</strong> - Total number of unique users with access</li>
          </ul>

          <h6 className="mt-3">Understanding Multi-Group Access:</h6>
          <p>
            If a user is in multiple groups that all have access to the same runbook, you'll see multiple
            group badges next to their name. They only need to be in one group to see the runbook.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Use Cases</h4>

          <h6 className="mt-3">User Permissions Lookup</h6>
          <ul>
            <li><strong>New User Onboarding</strong> - Verify a new user has the correct permissions</li>
            <li><strong>Access Requests</strong> - Check if a user already has a requested permission</li>
            <li><strong>Troubleshooting</strong> - Determine why a user can or cannot perform an action</li>
            <li><strong>Permission Audits</strong> - Review what access specific users have</li>
            <li><strong>Role Changes</strong> - Before/after comparison when changing user's groups</li>
          </ul>

          <h6 className="mt-3">Runbook Access Lookup</h6>
          <ul>
            <li><strong>Security Review</strong> - See who has access to sensitive runbooks</li>
            <li><strong>Access Expansion</strong> - Identify groups to add when more users need access</li>
            <li><strong>Troubleshooting</strong> - Find out why a user can or cannot see a runbook</li>
            <li><strong>Documentation</strong> - Document which teams have access to which workflows</li>
            <li><strong>Cleanup</strong> - Identify runbooks with too many or too few users</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Example Scenarios</h4>

          <h6 className="mt-3">Scenario 1: New User Can't See Runbooks</h6>
          <ol>
            <li>Open <strong>User Permissions Lookup</strong></li>
            <li>Select the user</li>
            <li>Check if they're in any groups (should show at least one)</li>
            <li>Switch to <strong>Runbook Access Lookup</strong></li>
            <li>Select the runbook they should see</li>
            <li>Check if any of their groups appear in the access list</li>
            <li>If not, assign the runbook to one of their groups</li>
          </ol>

          <h6 className="mt-3">Scenario 2: User Has Unexpected Permissions</h6>
          <ol>
            <li>Open <strong>User Permissions Lookup</strong></li>
            <li>Select the user</li>
            <li>Look at the <strong>Effective Permissions</strong> section</li>
            <li>Find the unexpected permission</li>
            <li>Check the group badge next to it to see which group grants it</li>
            <li>Either remove the permission from that group or remove the user from the group</li>
          </ol>

          <h6 className="mt-3">Scenario 3: Audit Sensitive Runbook Access</h6>
          <ol>
            <li>Open <strong>Runbook Access Lookup</strong></li>
            <li>Select the sensitive runbook</li>
            <li>Review the complete list of users with access</li>
            <li>Verify each user should have access</li>
            <li>Check which groups grant access</li>
            <li>Remove the runbook from groups with too broad access if needed</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Search Features</h4>
          <p>
            Both lookup tabs include search functionality to quickly find specific users or runbooks:
          </p>

          <h6 className="mt-3">User Search</h6>
          <p>Search by:</p>
          <ul>
            <li>First name</li>
            <li>Last name</li>
            <li>Email address</li>
          </ul>

          <h6 className="mt-3">Runbook Search</h6>
          <p>Search by:</p>
          <ul>
            <li>Runbook name</li>
            <li>Description</li>
            <li>Runbook ID</li>
          </ul>

          <p className="mt-3">
            <strong>Tip:</strong> Use search when you have many users or runbooks to make finding the right one faster.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li><strong>Regular Audits</strong> - Use these tools quarterly to review access patterns</li>
            <li><strong>Document Findings</strong> - Screenshot unusual access patterns for your records</li>
            <li><strong>Before Changes</strong> - Check current state before modifying groups or permissions</li>
            <li><strong>After Changes</strong> - Verify the changes had the intended effect</li>
            <li><strong>User Support</strong> - Use these when users report access issues</li>
            <li><strong>Compliance</strong> - Use for access reviews required by security policies</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding the Data</h4>

          <h6 className="mt-3">Group Status Matters</h6>
          <p>
            Remember that group status affects access:
          </p>
          <ul>
            <li><strong>Active Groups</strong> - Permissions and runbook access are in effect</li>
            <li><strong>Inactive Groups</strong> - Shown in lookup but do not grant actual access</li>
          </ul>
          <p className="mt-3">
            If you see a user in an inactive group, their effective permissions and runbook access
            will not include items from that group.
          </p>

          <h6 className="mt-3">Real-Time Data</h6>
          <p>
            The lookups show current state:
          </p>
          <ul>
            <li>Data refreshes when you select a different user or runbook</li>
            <li>Changes to groups, permissions, or runbook assignments are reflected immediately</li>
            <li>Refresh your browser if you don't see recent changes</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Limitations</h4>
          <ul>
            <li>Lookups show only users and runbooks in your client organization</li>
            <li>Admin access is not shown (they have universal access)</li>
            <li>Historical access is not tracked - you only see current state</li>
            <li>Export functionality is not currently available</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Related Topics</h4>
          <p>For more information on managing permissions, see:</p>
          <ul>
            <li><strong>Permission Groups Overview</strong> - Understanding the permission system</li>
            <li><strong>Managing Group Members</strong> - Adding/removing users from groups</li>
            <li><strong>Assigning Permissions</strong> - Adding permissions to groups</li>
            <li><strong>Assigning Runbooks to Groups</strong> - Controlling runbook visibility</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
}

export default PermissionLookups;
